/**
 * Subscription bundle: line item properties + per-group validation for custom-subscription-system block.
 */

/** @type {boolean} */
let sellingPlanVariantSyncAttached = false;

/**
 * Keep `selling_plan` in sync when the storefront fires `variant:update` (e.g. variant picker).
 * Subscription-only variants require a selling plan or the cart API returns 422.
 *
 * @param {Event} event
 */
function syncSubscriptionSellingPlans(event) {
  const detail = /** @type {CustomEvent} */ (event).detail;
  const productId = detail?.data?.productId;
  const variantId = detail?.resource?.id;
  if (productId == null || variantId == null) return;

  document.querySelectorAll('[data-subscription-bundle-root]').forEach((root) => {
    if (String(/** @type {HTMLElement} */ (root).dataset.productId || '') !== String(productId)) return;

    const planScript = root.querySelector('script[data-subscription-variant-selling-plans]');
    if (!planScript?.textContent) return;

    let map;
    try {
      map = JSON.parse(planScript.textContent.trim() || '{}');
    } catch {
      return;
    }

    const planId = map[String(variantId)];
    const formId = /** @type {HTMLElement} */ (root).dataset.formId;
    if (!formId) return;

    const form = document.getElementById(formId);
    if (!form) return;

    let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="selling_plan"]'));

    if (planId != null && planId !== '') {
      const value = String(planId);
      if (!input) {
        input = /** @type {HTMLInputElement} */ (document.createElement('input'));
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.setAttribute('data-subscription-selling-plan-input', '');
        form.appendChild(input);
      }
      input.value = value;
    } else if (input) {
      input.remove();
    }
  });
}

/**
 * Cart line property value: product title + SKU (not SKU alone).
 *
 * @param {string} label
 * @param {string} sku
 * @returns {string}
 */
function formatLineItemPropertyValue(label, sku) {
  const t = (label || '').trim();
  const s = (sku || '').trim();
  if (t && s) return `${t} (${s})`;
  if (t) return t;
  return s;
}

/**
 * @param {HTMLElement} propertiesHost
 * @param {string} groupId
 */
function clearGroupInputs(propertiesHost, groupId) {
  for (const el of propertiesHost.querySelectorAll('input[type="hidden"]')) {
    const h = /** @type {HTMLElement} */ (el);
    if (h.dataset.bundleGroup === groupId) h.remove();
  }
}

/**
 * How many bundle “slots” a group accounts for (merged multi-select uses one input + data-bundle-selection-count).
 *
 * @param {HTMLElement} propertiesHost
 * @param {string} groupId
 * @returns {number}
 */
function countBundleSelections(propertiesHost, groupId) {
  let n = 0;
  for (const el of propertiesHost.querySelectorAll('input[type="hidden"]')) {
    const h = /** @type {HTMLElement} */ (el);
    if (h.dataset.bundleGroup !== groupId) continue;
    const c = h.dataset.bundleSelectionCount;
    n += c ? parseInt(c, 10) || 1 : 1;
  }
  return n;
}

/**
 * @param {HTMLElement} root
 */
function initSubscriptionBundle(root) {
  const rawFormId = root.dataset.formId;
  if (typeof rawFormId !== 'string' || rawFormId === '') return;
  /** @type {string} */
  const formId = rawFormId;

  const form = document.getElementById(formId);
  const propertiesHostNode = root.querySelector('.subscription-bundle__properties-host');
  const addToCart = root.querySelector('add-to-cart-component');
  const summaryList = root.querySelector('[data-subscription-summary-list]');
  const totalMiddle = root.querySelector('[data-subscription-total-middle]');
  const totalMiddleTemplate = root.dataset.totalMiddleTemplate || '';
  const progressBar = root.querySelector('[data-subscription-progress-bar]');
  const progressWrap = root.querySelector('[data-subscription-progress]');
  const statusRegion = root.querySelector('[data-subscription-bundle-status]');
  const maxSelectionsMessage = root.dataset.maxSelectionsMessage || '';

  if (!form || !propertiesHostNode) return;

  const propertiesHost = /** @type {HTMLElement} */ (propertiesHostNode);

  /** @type {Map<string, Set<HTMLElement>>} */
  const pickSelections = new Map();

  const groups = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('.bundle-group')]);

  /**
   * @param {string} message
   */
  function announce(message) {
    if (!statusRegion) return;
    statusRegion.textContent = message;
  }

  /**
   * @param {HTMLElement} group
   * @param {string} name
   * @param {string} value
   * @param {string} sku
   * @param {string} label
   * @param {string} [imageUrl]
   * @param {number} [selectionCount] when >1, merged property; used for validation counts
   */
  function createHiddenInput(group, name, value, sku, label, imageUrl, selectionCount) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.setAttribute('form', formId);
    input.name = `properties[${name}]`;
    input.value = value;
    input.dataset.bundleGroup = group.dataset.bundleGroupId || '';
    input.dataset.sku = sku;
    input.dataset.label = label;
    if (imageUrl) input.dataset.imageUrl = imageUrl;
    if (selectionCount != null && selectionCount > 1) {
      input.dataset.bundleSelectionCount = String(selectionCount);
    }
    propertiesHost.appendChild(input);
  }

  /**
   * @param {HTMLElement} group
   */
  function rebuildAutoGroup(group) {
    const groupId = group.dataset.bundleGroupId || '';
    const base = group.dataset.propertyName || 'Selection';

    clearGroupInputs(propertiesHost, groupId);

    const cards = [...group.querySelectorAll('[data-bundle-product-card]')];
    const baseTrim = (base || 'Selection').trim();
    const parts = [];
    let firstImage = '';
    for (const card of cards) {
      const el = /** @type {HTMLElement} */ (card);
      const sku = el.dataset.sku || '';
      const label = el.dataset.label || '';
      if (!firstImage && el.dataset.imageUrl) firstImage = el.dataset.imageUrl;
      parts.push(formatLineItemPropertyValue(label, sku));
    }
    if (parts.length === 0) return;
    const merged = parts.join('; ');
    const n = parts.length;
    createHiddenInput(group, baseTrim, merged, '', merged, firstImage || undefined, n > 1 ? n : undefined);
  }

  /**
   * @param {HTMLElement} group
   */
  function rebuildPickGroup(group) {
    const groupId = group.dataset.bundleGroupId || '';
    const base = group.dataset.propertyName || 'Selection';
    const selected = pickSelections.get(groupId);
    if (!selected) return;

    clearGroupInputs(propertiesHost, groupId);

    const baseTrim = (base || 'Selection').trim();
    const cards = [...selected];
    if (cards.length === 0) return;

    const parts = [];
    let firstImage = '';
    for (const card of cards) {
      const el = /** @type {HTMLElement} */ (card);
      const sku = el.dataset.sku || '';
      const label = el.dataset.label || '';
      if (!firstImage && el.dataset.imageUrl) firstImage = el.dataset.imageUrl;
      parts.push(formatLineItemPropertyValue(label, sku));
    }
    const merged = parts.join('; ');
    const n = parts.length;
    createHiddenInput(group, baseTrim, merged, '', merged, firstImage || undefined, n > 1 ? n : undefined);
  }

  function updateSummary() {
    if (!summaryList) return;

    summaryList.innerHTML = '';
    let totalItems = 0;

    for (const group of groups) {
      const groupId = group.dataset.bundleGroupId || '';
      const auto = group.dataset.autoSelectAll === 'true';
      /** @type {HTMLElement[]} */
      let cards;
      if (auto) {
        cards = /** @type {HTMLElement[]} */ ([...group.querySelectorAll('[data-bundle-product-card]')]);
      } else {
        const sel = pickSelections.get(groupId);
        cards = sel ? /** @type {HTMLElement[]} */ ([...sel]) : [];
      }
      for (const card of cards) {
        totalItems += 1;
        const el = /** @type {HTMLElement} */ (card);
        const labelText = el.dataset.label || '';
        const sku = el.dataset.sku || '';
        const imgUrl = el.dataset.imageUrl;
        const li = document.createElement('li');
        li.className = 'subscription-bundle__selected-item';

        if (imgUrl) {
          const img = document.createElement('img');
          img.src = imgUrl;
          img.alt = labelText;
          img.className = 'subscription-bundle__selected-thumb';
          img.width = 40;
          img.height = 40;
          li.appendChild(img);
        }

        const span = document.createElement('span');
        span.className = 'subscription-bundle__selected-title';
        span.textContent = labelText;
        li.appendChild(span);
        summaryList.appendChild(li);
      }
    }

    if (totalMiddle && totalMiddleTemplate.includes('__COUNT__')) {
      totalMiddle.textContent = totalMiddleTemplate.replace('__COUNT__', String(totalItems));
    }
  }

  /**
   * @returns {boolean}
   */
  function allGroupsValid() {
    if (groups.length === 0) return true;

    let ok = true;
    for (const group of groups) {
      const groupId = group.dataset.bundleGroupId || '';
      const auto = group.dataset.autoSelectAll === 'true';
      const cardCount = parseInt(group.dataset.cardCount || '0', 10) || 0;
      const maxSelect = parseInt(group.dataset.maxSelect || '0', 10) || 0;
      const count = countBundleSelections(propertiesHost, groupId);

      if (cardCount === 0) {
        ok = false;
        continue;
      }

      if (auto) {
        if (count !== cardCount) ok = false;
      } else if (count !== maxSelect) {
        ok = false;
      }
    }
    return ok;
  }

  function updateProgress() {
    if (!progressBar || !progressWrap) return;
    let valid = 0;
    for (const group of groups) {
      const groupId = group.dataset.bundleGroupId || '';
      const auto = group.dataset.autoSelectAll === 'true';
      const cardCount = parseInt(group.dataset.cardCount || '0', 10) || 0;
      const maxSelect = parseInt(group.dataset.maxSelect || '0', 10) || 0;
      const count = countBundleSelections(propertiesHost, groupId);

      if (cardCount === 0) continue;
      if (auto) {
        if (count === cardCount) valid += 1;
      } else if (count === maxSelect) {
        valid += 1;
      }
    }
    const total = groups.length || 1;
    const pct = Math.round((valid / total) * 100);
    /** @type {HTMLElement} */ (progressBar).style.width = `${pct}%`;
    progressWrap.setAttribute('aria-valuenow', String(pct));
  }

  function updateAddToCartState() {
    const valid = allGroupsValid();
    root.dataset.bundleValid = valid ? 'true' : 'false';

    if (addToCart) {
      const cart = /** @type {HTMLElement & { enable?: () => void; disable?: () => void }} */ (
        addToCart
      );
      if (valid) {
        if (typeof cart.enable === 'function') cart.enable();
      } else if (typeof cart.disable === 'function') {
        cart.disable();
      }
    }

    updateSummary();
    updateProgress();
  }

  /**
   * @param {HTMLElement} group
   * @param {HTMLElement} card
   * @param {boolean} select
   */
  function setCardSelected(group, card, select) {
    const btn = card.querySelector('[data-bundle-product-toggle]');
    card.classList.toggle('bundle-product-card--selected', select);
    if (btn) {
      btn.setAttribute('aria-pressed', select ? 'true' : 'false');
    }
  }

  for (const group of groups) {
    const groupId = group.dataset.bundleGroupId || '';
    const auto = group.dataset.autoSelectAll === 'true';

    if (auto) {
      rebuildAutoGroup(group);
      group.querySelectorAll('[data-bundle-product-card]').forEach((card) => {
        setCardSelected(group, /** @type {HTMLElement} */ (card), true);
      });
    } else {
      pickSelections.set(groupId, new Set());
    }
  }

  root.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const toggle = target.closest('[data-bundle-product-toggle]');
    if (!toggle || !root.contains(toggle)) return;

    const card = /** @type {HTMLElement | null} */ (toggle.closest('[data-bundle-product-card]'));
    const group = /** @type {HTMLElement | null} */ (toggle.closest('.bundle-group'));
    if (!card || !group || group.dataset.autoSelectAll === 'true') return;

    const groupId = group.dataset.bundleGroupId || '';
    const maxSelect = parseInt(group.dataset.maxSelect || '1', 10) || 1;
    const selected = pickSelections.get(groupId);
    if (!selected) return;

    const isSelected = card.classList.contains('bundle-product-card--selected');

    if (isSelected) {
      selected.delete(card);
      setCardSelected(group, card, false);
      rebuildPickGroup(group);
      updateAddToCartState();
      announce('');
      return;
    }

    if (selected.size >= maxSelect) {
      announce(maxSelectionsMessage);
      return;
    }

    selected.add(card);
    setCardSelected(group, card, true);
    rebuildPickGroup(group);
    updateAddToCartState();
    announce('');
  });

  updateAddToCartState();
}

function boot() {
  if (!sellingPlanVariantSyncAttached) {
    sellingPlanVariantSyncAttached = true;
    document.addEventListener('variant:update', syncSubscriptionSellingPlans);
  }

  document.querySelectorAll('[data-subscription-bundle-root]').forEach((root) => {
    initSubscriptionBundle(/** @type {HTMLElement} */ (root));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
